/**
 * Job Detail component
 * Displays comprehensive job posting information with tenant-branded design
 */
import React from 'react'
import type { JobPosting } from '@/types'
import { ApplyButton } from './ApplyButton'
import { useTranslation } from '@/hooks/useTranslation'
import { useTheme } from '@/lib/theme/theme-provider'

export interface JobDetailProps {
  jobPosting: JobPosting
}

/**
 * Formats salary range with currency
 */
function formatSalaryRange(
  min: number | undefined,
  max: number | undefined,
  currency: string = 'R$'
): string | null {
  if (min === undefined || max === undefined) {
    return null
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num)
  }

  return `${currency} ${formatNumber(min)} - ${currency} ${formatNumber(max)}`
}

export const JobDetail: React.FC<JobDetailProps> = ({ jobPosting }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const salaryRange = formatSalaryRange(
    jobPosting.salaryMin,
    jobPosting.salaryMax,
    jobPosting.salaryCurrency
  )

  return (
    <article className="min-h-screen pb-24 md:pb-8">
      {/* Dark Hero Section */}
      <header className="bg-surface">
        <div className="max-w-4xl mx-auto px-4 pt-8 pb-12">
          {/* Logo + Tagline */}
          {theme?.logo && (
            <div className="mb-8">
              <img
                src={theme.logo.horizontal}
                alt={theme.displayName}
                className="h-8 md:h-10 w-auto"
              />
            </div>
          )}

          <h1 className="text-3xl md:text-4xl font-bold font-heading text-ds-text mb-4">
            {jobPosting.title}
          </h1>

          {/* Badges on dark background */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-surface-elevated text-ds-text border border-surface-border">
              {jobPosting.department}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-surface-elevated text-ds-text border border-surface-border">
              {jobPosting.location}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/15 text-primary border border-primary/30">
              {t(`job.workMode.${jobPosting.workMode}`)}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/15 text-primary border border-primary/30">
              {t(`job.employmentType.${jobPosting.employmentType}`)}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-surface-elevated text-ds-text border border-surface-border">
              {t(`job.seniority.${jobPosting.seniority}`)}
            </span>
          </div>

          {/* Desktop Apply Button in hero */}
          <div className="hidden md:block">
            <ApplyButton jobPostingId={jobPosting.jobPostingId} />
          </div>
        </div>
      </header>

      {/* Content sections on white cards */}
      <div className="max-w-4xl mx-auto px-4 -mt-4">
        <div className="bg-card rounded-lg shadow-lg border border-card-border overflow-hidden">
          <div className="p-6 md:p-8 space-y-8">
            {/* Description Section */}
            <section>
              <h2 className="text-2xl font-bold font-heading text-card-text mb-4">
                {t('job.sections.aboutRole')}
              </h2>
              <p className="text-card-text-secondary whitespace-pre-wrap leading-relaxed">
                {jobPosting.description}
              </p>
            </section>

            {/* Responsibilities Section */}
            {jobPosting.responsibilities.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold font-heading text-card-text mb-4">
                  {t('job.sections.responsibilities')}
                </h2>
                <ul className="space-y-2">
                  {jobPosting.responsibilities.map((responsibility, index) => (
                    <li key={index} className="flex items-start gap-3 text-card-text-secondary">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      {responsibility}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Requirements Section */}
            <section>
              <h2 className="text-2xl font-bold font-heading text-card-text mb-4">
                {t('job.sections.requirements')}
              </h2>

              {/* Technical Requirements */}
              {jobPosting.requirementsTechnical.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-card-text mb-3">
                    {t('job.sections.technicalSkills')}
                  </h3>
                  <ul className="space-y-2">
                    {jobPosting.requirementsTechnical.map((requirement, index) => (
                      <li key={index} className="flex items-start gap-3 text-card-text-secondary">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                        {requirement}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Behavioral Requirements */}
              {jobPosting.requirementsBehavioral.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-card-text mb-3">
                    {t('job.sections.behavioralSkills')}
                  </h3>
                  <ul className="space-y-2">
                    {jobPosting.requirementsBehavioral.map((requirement, index) => (
                      <li key={index} className="flex items-start gap-3 text-card-text-secondary">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                        {requirement}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* Benefits Section */}
            {jobPosting.benefits.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold font-heading text-card-text mb-4">
                  {t('job.sections.benefits')}
                </h2>
                <ul className="space-y-2">
                  {jobPosting.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3 text-card-text-secondary">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Salary Section */}
            {salaryRange && (
              <section className="bg-surface rounded-lg p-6">
                <h2 className="text-2xl font-bold font-heading text-ds-text mb-2">
                  {t('job.sections.compensation')}
                </h2>
                <p className="text-xl font-semibold text-primary">{salaryRange}</p>
              </section>
            )}
          </div>
        </div>

        {/* Tagline footer */}
        {theme && (
          <div className="text-center py-8">
            <p className="text-ds-text-secondary text-sm font-medium tracking-wider uppercase">
              {theme.tagline}
            </p>
          </div>
        )}
      </div>

      {/* Sticky Apply Button for Mobile */}
      <div className="md:hidden">
        <ApplyButton jobPostingId={jobPosting.jobPostingId} sticky />
      </div>
    </article>
  )
}
